import React, { useState } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useSnackbar } from "notistack";
import { resend_student_invite_api } from "../../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, accentSoft, font, success,
  primaryButtonSx, secondaryButtonSx,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * What a student's record shows before they have done anything.
 *
 * WHAT THIS REPLACED
 * Eleven zeroes and an accusation. The page rendered "0% covered", "0/16
 * lesson standards met", "£0 · 0 sessions", "Weakest areas (0)", nine blank
 * subject rows, "Nothing booked", "No open tasks", "Nothing here yet" — and an
 * amber "Watch · Has never opened a lesson", which blamed the student for the
 * instructor not having sent the invite yet.
 *
 * That is the first thing an instructor sees on every student they add, and it
 * said nothing they could act on. This says the three things that actually
 * move a new student forward, and every one of them is a button.
 *
 * THE CLOSING PARAGRAPH IS LOAD-BEARING
 * "Once they have answered a few questions this page will show you what they
 * are getting wrong" is a promise with a condition, not an apology for being
 * empty. It tells an instructor what the page becomes and what has to happen
 * first, which is the difference between a blank screen and a next step.
 */

function Step({ n, title, body, done, action }) {
  return (
    <Box sx={{
      display: "flex", gap: 1.75, alignItems: "flex-start",
      py: 1.75, borderTop: n === 1 ? "none" : `1px solid ${border}`,
    }}>
      <Box sx={{
        width: 24, height: 24, borderRadius: "50%", flexShrink: 0, mt: "1px",
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: done ? `${success}1a` : accentSoft,
        color: done ? success : accent,
        fontFamily: font, fontSize: "11.5px", fontWeight: 800,
      }}>
        {done ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : n}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontFamily: font, fontSize: "14px", fontWeight: 700,
          color: done ? textSecondary : textPrimary,
          textDecoration: done ? "line-through" : "none",
        }}>
          {title}
        </Typography>
        <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, lineHeight: 1.5, mt: 0.25 }}>
          {body}
        </Typography>
      </Box>
      {!done && action}
    </Box>
  );
}

const daysSince = (d) => (d ? Math.round((Date.now() - new Date(d)) / 864e5) : null);

export default function RecordGetStarted({ record, studentId, onPropose, onSetWork, onReload }) {
  const { enqueueSnackbar } = useSnackbar();
  const [resending, setResending] = useState(false);

  const student = record?.student || {};
  const firstName = (student.name || "").split(" ")[0] || "they";
  const course = record?.course;
  const nextSession = record?.openItems?.nextSession;

  // "Invited and ignoring it" and "accepted but never started" are different
  // conversations, and one of them is the instructor's to fix.
  const accepted = Boolean(student.inviteAcceptedAt) || student.status !== "pending";
  const sentDays = daysSince(student.inviteSentAt);

  const handleResend = async () => {
    setResending(true);
    try {
      const r = await resend_student_invite_api(studentId);
      enqueueSnackbar(r?.code === 200 ? `Invite resent to ${student.email}` : (r?.message || "Could not resend that invite"),
        { variant: r?.code === 200 ? "success" : "error" });
    } catch (e) {
      enqueueSnackbar("Could not resend that invite", { variant: "error" });
    } finally {
      setResending(false);
    }
  };

  // The first exam on their own course — not a hardcoded one. For a UK student
  // that is Air Law, which is also the one they must pass before first solo.
  //
  // The fallback reads fine in the sentence below and nowhere else. It used to
  // be interpolated into the task itself too, which sent a student a task
  // called "Start their first subject" whenever the course had no first lesson
  // — so the composer opens with the real subject list instead of guessing.
  const firstSubject = course?.firstLessonTitle || "their first subject";


  return (
    <Box sx={{
      backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "12px",
      p: { xs: 2, sm: 2.5 }, mb: 2,
    }}>
      <Typography sx={{
        fontFamily: font, fontSize: "11px", fontWeight: 800, letterSpacing: "0.07em",
        textTransform: "uppercase", color: textSecondary, mb: 0.5,
      }}>
        Get {firstName} started
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "13px", color: textSecondary, mb: 1.5, lineHeight: 1.55 }}>
        {accepted
          ? `${firstName} has an account but hasn't done any work yet.`
          : sentDays != null
            ? `Invited ${sentDays === 0 ? "today" : `${sentDays} day${sentDays === 1 ? "" : "s"} ago`}, not opened yet.`
            : `${firstName} hasn't been invited yet.`}
      </Typography>

      <Step
        n={1}
        title={accepted ? "Account activated" : "Send the invite"}
        body={accepted
          ? `${firstName} has accepted and can sign in.`
          : `${firstName} can't do anything until they've activated their account.`}
        done={accepted}
        action={
          <Button onClick={handleResend} disabled={resending} sx={{ ...primaryButtonSx, minHeight: 32, padding: "5px 14px", fontSize: "12.5px" }}>
            {resending ? <CircularProgress size={13} sx={{ color: "#fff" }} /> : (sentDays != null ? "Resend" : "Send invite")}
          </Button>
        }
      />

      <Step
        n={2}
        title="Book the first lesson"
        body={nextSession
          ? "Booked — nothing more to do here."
          : "A date in the diary is the thing that makes anyone start."}
        done={Boolean(nextSession)}
        action={
          <Button onClick={onPropose} sx={{ ...secondaryButtonSx, minHeight: 32, padding: "5px 14px", fontSize: "12.5px" }}>
            Find a time
          </Button>
        }
      />

      <Step
        n={3}
        title="Point them at their first subject"
        body={`Most instructors start with ${firstSubject}.`}
        done={(record?.openItems?.tasks || []).length > 0}
        action={
          <Button onClick={onSetWork} sx={{ ...secondaryButtonSx, minHeight: 32, padding: "5px 14px", fontSize: "12.5px" }}>
            Set work
          </Button>
        }
      />

      <Typography sx={{
        fontFamily: font, fontSize: "12px", color: textMuted, lineHeight: 1.6,
        mt: 1.75, pt: 1.5, borderTop: `1px solid ${border}`,
      }}>
        Once {firstName} has answered a few questions, this page shows what they keep
        getting wrong — topic by topic, with the actual wrong answers — and what to cover next.
      </Typography>
    </Box>
  );
}
